import { useMutation } from '@tanstack/react-query'
import type { AdminSetting, SettingGroupName } from '@ytclip/shared'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { updateSettings } from '@/features/admin/api.ts'
import { useAdminSettings } from '@/features/shared-hooks.ts'
import { ApiRequestError } from '@/lib/api-client.ts'

/** A field counts as changed only when it differs from what is stored. */
export function isDirty(setting: AdminSetting, draft: Record<string, string>): boolean {
  const value = draft[setting.key]

  return value !== undefined && value !== setting.value
}

export interface SettingsDraft {
  groups: { group: SettingGroupName; label: string; settings: AdminSetting[] }[]
  draft: Record<string, string>
  setValue: (key: string, value: string) => void
  changed: AdminSetting[]
  save: () => void
  saving: boolean
  isPending: boolean
  isError: boolean
}

/**
 * The shared editing surface behind every settings screen.
 *
 * Only fields that actually differ from what is stored are sent, which is what
 * keeps a save from overwriting a value another admin changed in a group this
 * screen is not even showing.
 *
 * `keep` narrows which groups a screen owns: the general page takes everything
 * except the desktop-app groups, and the desktop-app console takes exactly
 * those. The same draft machinery serves both.
 */
export function useSettingsDraft(keep: (group: SettingGroupName) => boolean): SettingsDraft {
  const { data, isPending, isError, refetch } = useAdminSettings()
  const [draft, setDraft] = useState<Record<string, string>>({})

  const groups = useMemo(
    () => (data?.data ?? []).filter((group) => keep(group.group)),
    [data, keep],
  )

  const changed = useMemo(
    () => groups.flatMap((group) => group.settings).filter((setting) => isDirty(setting, draft)),
    [groups, draft],
  )

  const save = useMutation({
    mutationFn: () =>
      updateSettings({
        updates: changed.map((setting) => ({ key: setting.key, value: draft[setting.key] ?? '' })),
      }),
    onSuccess: () => {
      toast.success('Pengaturan tersimpan')
      setDraft({})
      void refetch()
    },
    onError: (error) =>
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Gagal menyimpan pengaturan',
      ),
  })

  return {
    groups,
    draft,
    setValue: (key, value) => setDraft((current) => ({ ...current, [key]: value })),
    changed,
    save: () => save.mutate(),
    saving: save.isPending,
    isPending,
    isError,
  }
}
